import React, { useEffect, useState, useRef, useMemo } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { pollImportJob } from "@/lib/importJob";
import { Library, Trash2, Zap, Plus, ShieldCheck, Check, FileUp, Loader2, Fingerprint, UploadCloud, Link2, RefreshCw, Cloud, BarChart3, AlertTriangle, Search, Hash, ShieldAlert, Wand2, BookOpen, Layers, Sigma, Activity, Wrench, Brain, Archive, ChevronDown, Eye, Star, FolderUp } from "lucide-react";
import { SmartImportWizard, KeywordDictionary } from "@/components/SmartImport";
import { MultiVariantImport } from "@/components/MultiVariantImport";
import { SolutionCombiner } from "@/components/SolutionCombiner";
import { ChecksumLearning } from "@/components/ChecksumLearning";
import { VehicleCatalog } from "@/components/VehicleCatalog";
import { EcuCatalog } from "@/components/EcuCatalog";
import { TuningStateChecker } from "@/components/TuningStateChecker";
import { SolutionDetail } from "@/components/SolutionDetail";
import { ModImport } from "@/components/ModImport";

const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");

const ReliabilityBadge = ({ s }) => {
  const rel = s.reliability;
  const deliveries = s.deliveries || 0;
  const defects = s.defects || 0;
  if (rel === null || rel === undefined) {
    return <span data-testid={`reliability-${s.id}`} className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-white/5 text-zinc-500" title="Jamais livré">Nouveau</span>;
  }
  const cls = rel >= 90 ? "bg-signal-success/15 text-signal-success" : rel >= 70 ? "bg-signal-processing/15 text-signal-processing" : "bg-signal-error/15 text-signal-error";
  const tip = `${defects} défaut${defects > 1 ? "s" : ""} sur ${deliveries} livraison${deliveries > 1 ? "s" : ""}${s.last_defect_at ? ` · dernier le ${fmtD(s.last_defect_at)}` : ""}`;
  return (
    <div className="flex items-center gap-1.5">
      <span data-testid={`reliability-${s.id}`} title={tip} className={`flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm cursor-help ${cls}`}>
        {rel >= 90 ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
        {rel}% · {deliveries} livr.
      </span>
      {defects > 0 && (
        <span data-testid={`defect-badge-${s.id}`} title="Problème signalé sur ce fichier" className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-signal-error/20 text-signal-error animate-pulse">
          <AlertTriangle className="w-3 h-3" /> Défaut
        </span>
      )}
      {s.avg_rating != null && (
        <span data-testid={`satisfaction-${s.id}`} title={`${s.ratings} avis client`} className="flex items-center gap-0.5 font-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-yellow-400/10 text-yellow-400">
          <Star className="w-3 h-3 fill-yellow-400" /> {s.avg_rating}
        </span>
      )}
    </div>
  );
};

const AUTO_IDS = ["stage1", "stage2", "fap_off", "egr_off", "adblue_off", "dtc_off", "o2_off", "popbang"];

const TABS = [
  { id: "library", label: "Bibliothèque", icon: Library },
  { id: "import", label: "Importer", icon: UploadCloud },
  { id: "analytics", label: "Vue d'ensemble", icon: Activity },
  { id: "tools", label: "Outils", icon: Wrench },
];

const IMPORT_METHODS = [
  { id: "ai", title: "Import intelligent", desc: "Assistant IA — détecte tout", icon: Brain, color: "aurora-purple" },
  { id: "variantes", title: "Original + variantes", desc: "1 ORI → N MODs d'un coup", icon: Layers, color: "aurora-cyan" },
  { id: "mod", title: "MOD seul", desc: "Reconnaissance auto de l'ORI", icon: Wand2, color: "aurora-cyan" },
  { id: "paire", title: "Importer une paire", desc: "1 original + 1 modifié", icon: Link2, color: "aurora-cyan" },
  { id: "masse", title: "Import en masse", desc: "Archive .zip / .rar / .7z ou lien URL", icon: Archive, color: "aurora-purple" },
];

export const AdminSolutions = () => {
  const [solutions, setSolutions] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("library");
  const [importMethod, setImportMethod] = useState(null);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({ brand: "", ecu: "", services: [], tuned_with: "", power_hp: "" });
  const [origFile, setOrigFile] = useState(null);
  const [modFile, setModFile] = useState(null);
  const [bulk, setBulk] = useState({ brand: "", ecu: "", services: [], url: "" });
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [folderFiles, setFolderFiles] = useState([]);
  const [folderProgress, setFolderProgress] = useState(null);
  const [preview, setPreview] = useState(null); // { jobId, pairs, skipped }
  const [proSummary, setProSummary] = useState(null); // { imported, attempted, skipped:[] }
  const [committing, setCommitting] = useState(false);
  const [bulkApply, setBulkApply] = useState({ brand: "", model: "", ecu: "", power_hp: "", torque_nm: "", stage: "" });
  const applyToAll = () => {
    setPreview((pv) => ({ ...pv, pairs: pv.pairs.map((p) => ({
      ...p,
      ...(bulkApply.brand.trim() ? { brand: bulkApply.brand.trim() } : {}),
      ...(bulkApply.model.trim() ? { model: bulkApply.model.trim() } : {}),
      ...(bulkApply.ecu.trim() ? { ecu: bulkApply.ecu.trim() } : {}),
      ...(String(bulkApply.power_hp).trim() ? { power_hp: String(bulkApply.power_hp).trim() } : {}),
      ...(String(bulkApply.torque_nm).trim() ? { torque_nm: String(bulkApply.torque_nm).trim() } : {}),
      ...(bulkApply.stage.trim() ? { stage: bulkApply.stage.trim() } : {}),
    })) }));
    toast.success("Appliqué à toutes les lignes");
  };
  const [catBrands, setCatBrands] = useState([]);
  const [catModels, setCatModels] = useState({}); // brandLower -> [modelNames]
  useEffect(() => {
    api.get("/catalog/vehicles").then(({ data }) => {
      const brands = (data.brands || []).map((b) => b.brand);
      const map = {};
      (data.brands || []).forEach((b) => { map[b.brand.toLowerCase()] = (b.models || []).map((m) => m.name); });
      setCatBrands(brands); setCatModels(map);
    }).catch(() => {});
  }, []);
  const [jobs, setJobs] = useState([]);
  const [cov, setCov] = useState(null);
  const origRef = useRef(null);
  const modRef = useRef(null);
  const bulkRef = useRef(null);
  const folderRef = useRef(null);
  const [query, setQuery] = useState("");
  const [detailId, setDetailId] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return solutions;
    return solutions.filter((s) =>
      [s.ref, s.brand, s.ecu, s.mod_filename, s.original_filename, (s.services || []).join(" "), s.original_hash]
        .some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [solutions, query]);

  const flagged = useMemo(
    () => solutions.filter((s) => s.reliability !== null && s.reliability !== undefined && s.reliability < 90),
    [solutions]
  );

  const grouped = useMemo(() => {
    const m = new Map();
    for (const s of filtered) {
      const k = s.original_hash || s.id;
      if (!m.has(k)) m.set(k, { hash: k, brand: s.brand, model: s.model, ecu: s.ecu, size: s.original_size, ori_filename: "", variants: [] });
      const g = m.get(k);
      g.variants.push(s);
      if (!g.ori_filename && s.original_filename) g.ori_filename = s.original_filename;
      if (!g.brand && s.brand) g.brand = s.brand;
      if (!g.model && s.model) g.model = s.model;
      if (!g.ecu && s.ecu) g.ecu = s.ecu;
    }
    return Array.from(m.values());
  }, [filtered]);

  const load = () => api.get("/admin/solutions").then(({ data }) => setSolutions(data)).finally(() => setLoading(false));
  const loadJobs = () => api.get("/admin/solutions/import_jobs").then(({ data }) => setJobs(data)).catch(() => {});
  const loadCov = () => api.get("/admin/solutions/coverage").then(({ data }) => setCov(data)).catch(() => {});
  useEffect(() => {
    load();
    loadJobs();
    loadCov();
    api.get("/catalog").then(({ data }) => setServices(data.services.filter((s) => AUTO_IDS.includes(s.id))));
    const t = setInterval(() => { loadJobs(); loadCov(); }, 4000);
    return () => clearInterval(t);
  }, []);

  const toggleBulkSvc = (id) => setBulk((b) => ({ ...b, services: b.services.includes(id) ? b.services.filter((x) => x !== id) : [...b.services, id] }));
  const toggleSvc = (id) => setForm((f) => ({ ...f, services: f.services.includes(id) ? f.services.filter((x) => x !== id) : [...f.services, id] }));

  const prefillImport = (m) => {
    setTab("import");
    setImportMethod("masse");
    setBulk((b) => ({ ...b, brand: m.brand === "—" ? "" : m.brand, ecu: m.ecu || "" }));
    setTimeout(() => document.querySelector('[data-testid="bulk-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 160);
  };

  const submitBulk = async () => {
    if (!bulkFile && !bulk.url.trim()) return toast.error("Ajoutez un fichier .zip / .rar / .7z ou un lien de téléchargement");
    setBulkBusy(true);
    const fd = new FormData();
    if (bulkFile) fd.append("file", bulkFile);
    if (bulk.url.trim()) fd.append("source_url", bulk.url.trim());
    fd.append("brand", bulk.brand);
    fd.append("ecu", bulk.ecu);
    fd.append("services", JSON.stringify(bulk.services));
    try {
      await api.post("/admin/solutions/bulk_import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Import lancé — indexation en cours");
      setBulkFile(null); setBulk({ brand: "", ecu: "", services: [], url: "" });
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'import");
    } finally { setBulkBusy(false); }
  };

  // Mirror the backend `_is_import_file`: accept ECU binaries INCLUDING WinOLS/label-style
  // extensions (.Original, .Stage1, .Stage1+++, .EGR, numeric tags…), while rejecting real
  // non-binary files. Keep this in sync with server.py so folder-upload never drops a valid ORI/MOD.
  const REL_OK = (n) => {
    const l = (n || "").toLowerCase();
    if (l === "thumbs.db" || l.endsWith(".ds_store")) return false;
    const SKIP = [".pdf", ".txt", ".csv", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg", ".ico", ".zip", ".rar", ".7z", ".gz", ".tar", ".log", ".xml", ".json", ".html", ".htm", ".md", ".ini", ".cfg", ".exe", ".dll", ".db", ".sqlite", ".mp3", ".mp4", ".mov", ".avi"];
    const IMPORT = [".bin", ".ori", ".mod", ".hex", ".dat", ".frf", ".kp"];
    const KW = ["ori", "orig", "original", "stock", "std", "read", "backup", "org", "mod", "modified", "tuned", "stage", "remap", "stg", "tun", "map", "egr", "dpf", "fap", "adblue", "o2", "dtc"];
    const dot = l.lastIndexOf(".");
    if (dot < 0) return true; // no extension -> raw dump
    const suf = l.slice(dot);
    if (SKIP.includes(suf)) return false;
    if (IMPORT.includes(suf)) return true;
    const label = suf.slice(1);
    if (!label) return true;
    if (KW.some((k) => label.includes(k))) return true;
    if (/^(stage|stg|st)\d*$/.test(label)) return true; // stage / stg2 / st3
    if (/^\d+$/.test(label)) return true;               // numeric tag e.g. .110
    if (/^[a-z0-9]+$/.test(label) && label.length <= 12) return true; // short WinOLS tag
    return false;
  };

  const uploadAndPreview = async (pro = false) => {
    const all = Array.from(folderFiles || []).filter((f) => REL_OK(f.name) && f.size > 0);
    if (all.length === 0) return toast.error("Sélectionnez un dossier contenant des fichiers .bin (ORI/MOD)");
    setBulkBusy(true);
    const totalBytes = all.reduce((s, f) => s + f.size, 0);
    // Pre-compute batches (max 5 files / 4MB) so we know the total count for the progress bar.
    const batches = [];
    { let b = [], bb = 0;
      for (const f of all) { b.push(f); bb += f.size; if (b.length >= 5 || bb >= 4 * 1024 * 1024) { batches.push(b); b = []; bb = 0; } }
      if (b.length) batches.push(b);
    }
    setFolderProgress({ done: 0, total: all.length, batch: 0, batches: batches.length, sentBytes: 0, totalBytes });
    try {
      const { data } = await api.post("/admin/solutions/folder_import/start", { brand: bulk.brand, ecu: bulk.ecu, services: bulk.services });
      const jobId = data.job_id;
      let done = 0, sentBytes = 0;
      for (let bi = 0; bi < batches.length; bi++) {
        const current = batches[bi];
        const fd = new FormData();
        const paths = [];
        current.forEach((f) => { fd.append("files", f); paths.push(f.webkitRelativePath || f.name); });
        fd.append("paths", JSON.stringify(paths));
        // Retry each batch up to 3 times to survive flaky networks / gateway hiccups.
        let lastErr;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            await api.post(`/admin/solutions/folder_import/${jobId}/batch`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
            lastErr = null; break;
          } catch (err) { lastErr = err; await new Promise((r) => setTimeout(r, 1000 * (attempt + 1))); }
        }
        if (lastErr) throw lastErr;
        done += current.length; sentBytes += current.reduce((s, f) => s + f.size, 0);
        setFolderProgress({ done, total: all.length, batch: bi + 1, batches: batches.length, sentBytes, totalBytes });
      }
      // Verify the server actually received every file before analyzing.
      try {
        const { data: js } = await api.get("/admin/solutions/import_jobs");
        const j = (js || []).find((x) => x.id === jobId);
        if (j && typeof j.received === "number" && j.received < all.length) {
          toast.warning(`${j.received}/${all.length} fichiers reçus — certains n'ont pas été envoyés (réseau). Réessayez si des paires manquent.`);
        }
      } catch (_) { /* non-blocking */ }
      const { data: prev } = await api.post(`/admin/solutions/folder_import/${jobId}/preview`);
      const pairs = (prev.pairs || []).map((p) => ({ ...p, _keep: true }));
      setFolderFiles([]); setFolderProgress(null);
      if (folderRef.current) folderRef.current.value = "";
      if (pairs.length === 0) { toast.error("Aucune paire ORI/MOD détectée dans ce dossier"); return; }
      if (pro) {
        // Mode professionnel : import direct de toutes les paires, sans revue manuelle.
        const analyzeSkipped = prev.skipped || [];
        await api.post(`/admin/solutions/folder_import/${jobId}/commit`, { pairs });
        // Poll the job until the background commit is done to get the real imported/skipped counts.
        let imported = pairs.length, jobSkipped = 0;
        for (let k = 0; k < 15; k++) {
          await new Promise((r) => setTimeout(r, 1000));
          try {
            const { data: js } = await api.get("/admin/solutions/import_jobs");
            const j = (js || []).find((x) => x.id === jobId);
            if (j && j.status === "done") { imported = j.imported ?? pairs.length; jobSkipped = j.skipped ?? 0; break; }
          } catch (_) { /* keep polling */ }
        }
        setProSummary({ imported, attempted: pairs.length, jobSkipped, skipped: analyzeSkipped });
        toast.success(`${imported} paire(s) importée(s) et validée(s) d'office (mode Pro)`);
        loadJobs();
        setPreview(null);
        return;
      }
      setPreview({ jobId, pairs, skipped: prev.skipped || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'analyse du dossier");
      setFolderProgress(null);
    } finally { setBulkBusy(false); }
  };

  const updatePair = (i, patch) => setPreview((pv) => ({ ...pv, pairs: pv.pairs.map((p, idx) => idx === i ? { ...p, ...patch } : p) }));
  const togglePairSvc = (i, sid) => setPreview((pv) => ({ ...pv, pairs: pv.pairs.map((p, idx) => idx === i ? { ...p, services: p.services.includes(sid) ? p.services.filter((x) => x !== sid) : [...p.services, sid] } : p) }));

  const isComplete = (p) => (p.brand || "").trim() && (p.model || "").trim() && (p.services || []).length > 0;

  const confirmComplete = async () => {
    const complete = preview.pairs.filter((p) => p._keep && isComplete(p));
    if (complete.length === 0) return toast.error("Aucune ligne complète (marque + modèle + solution requis)");
    const remaining = preview.pairs.filter((p) => !(p._keep && isComplete(p)));
    setCommitting(true);
    try {
      await api.post(`/admin/solutions/folder_import/${preview.jobId}/commit`, { pairs: complete, keep_files: remaining.length > 0 });
      toast.success(`${complete.length} ligne(s) complète(s) importée(s)`);
      if (remaining.length === 0) { setPreview(null); }
      else { setPreview({ ...preview, pairs: remaining }); toast.info(`${remaining.length} ligne(s) à compléter manuellement`); }
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setCommitting(false); }
  };

  const confirmImport = async () => {
    const toImport = preview.pairs.filter((p) => p._keep && (p.services || []).length > 0);
    if (toImport.length === 0) return toast.error("Aucune paire valide à importer (vérifiez les solutions)");
    setCommitting(true);
    try {
      await api.post(`/admin/solutions/folder_import/${preview.jobId}/commit`, { pairs: toImport });
      toast.success(`Import de ${toImport.length} paire(s) lancé`);
      setPreview(null);
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setCommitting(false); }
  };

  // --- Collision helpers (two pairs = same original + same services -> one would overwrite the other) ---
  const SVC_ALIASES = {
    popbang: ["pop&b", "p&b", "p_b", "popbang", "pop bang", "pop+b", "popandbang", "antilag", "anti-lag", "crackle", "burble"],
    egr_off: ["egr"],
    fap_off: ["fap", "dpf"],
    adblue_off: ["adblue", "ad-blue", "scr", "def"],
    dtc_off: ["dtc", "dtcoff"],
    o2_off: ["o2", "lambda", "catoff", "cat-off"],
    stage2: ["stage2", "stage_2", "stage 2", "stg2", "st2"],
    stage1: ["stage1", "stage_1", "stage 1", "stg1", "st1"],
  };
  const availableSvcIds = new Set((services || []).map((s) => s.id));
  const detectExtraServices = (name) => {
    const l = (name || "").toLowerCase();
    const out = [];
    for (const [sid, aliases] of Object.entries(SVC_ALIASES)) {
      if (availableSvcIds.has(sid) && aliases.some((a) => l.includes(a))) out.push(sid);
    }
    return out;
  };
  const collisionKey = (p) => `${p.ori_rel || p.ori_name}||${[...(p.services || [])].sort().join(",")}`;
  const getClashes = (pairs) => {
    const groups = {};
    (pairs || []).filter((p) => p._keep && (p.services || []).length > 0)
      .forEach((p) => { (groups[collisionKey(p)] = groups[collisionKey(p)] || []).push(p); });
    return Object.entries(groups).filter(([, arr]) => arr.length > 1);
  };
  // (a) Auto-detect a distinctive option (Pop&Bang / EGR / FAP / stage…) from each colliding MOD name.
  const autoDifferentiate = () => {
    setPreview((pv) => {
      const clashKeys = new Set(getClashes(pv.pairs).map(([k]) => k));
      let changed = 0;
      const pairs = pv.pairs.map((p) => {
        if (!(p._keep && clashKeys.has(collisionKey(p)))) return p;
        const merged = Array.from(new Set([...(p.services || []), ...detectExtraServices(p.mod_name)]));
        if (merged.length !== (p.services || []).length) changed++;
        return { ...p, services: merged };
      });
      setTimeout(() => {
        if (changed > 0) toast.success(`${changed} variante(s) différenciée(s) automatiquement`);
        else toast.info("Aucune option distinctive trouvée dans les noms — choisissez laquelle garder");
      }, 0);
      return { ...pv, pairs };
    });
  };
  // (b) Keep only one pair of a colliding group; uncheck its siblings (same original + same services).
  const keepOnlyInGroup = (modRel) => {
    setPreview((pv) => {
      const target = pv.pairs.find((p) => (p.mod_rel || p.mod_name) === modRel);
      if (!target) return pv;
      const key = collisionKey(target);
      const pairs = pv.pairs.map((p) => {
        if (collisionKey(p) !== key || !p._keep) return p;
        return { ...p, _keep: (p.mod_rel || p.mod_name) === modRel };
      });
      return { ...pv, pairs };
    });
  };


  const submitImport = async () => {
    if (!origFile || !modFile) return toast.error("Ajoutez le fichier original ET le fichier modifié");
    if (form.services.length === 0) return toast.error("Sélectionnez au moins une solution");
    setImporting(true);
    const fd = new FormData();
    fd.append("original_file", origFile);
    fd.append("modified_file", modFile);
    fd.append("brand", form.brand);
    fd.append("ecu", form.ecu);
    fd.append("services", JSON.stringify(form.services));
    fd.append("tuned_with", form.tuned_with || "");
    fd.append("power_hp", form.power_hp || "");
    try {
      const { data: started } = await api.post("/admin/solutions/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const job = await pollImportJob(started.job_id);
      if (job.status === "failed") {
        toast.error(job.fail_summary || job.error || "Erreur d'import");
      } else {
        const r = job.result || {};
        toast.success(r.diff_ok ? "Solution importée · différentiel calculé ✅" : "Solution importée (clone)");
        setForm({ brand: "", ecu: "", services: [], tuned_with: "", power_hp: "" }); setOrigFile(null); setModFile(null);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erreur d'import");
    } finally { setImporting(false); loadJobs(); }
  };

  const remove = async (id) => { await api.delete(`/admin/solutions/${id}`); toast.success("Solution supprimée"); load(); };

  const inputCls = "w-full bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none placeholder:text-zinc-600";

  const covKpis = cov ? [
    ["Solutions en base", cov.total, "text-aurora-cyan", Library],
    ["Taux livraison auto", `${cov.auto_delivery.rate}%`, "text-signal-success", Zap],
    ["Commandes totales", cov.auto_delivery.total_orders, "text-white", Fingerprint],
    ["ECU couverts", cov.by_ecu.filter((e) => e.name !== "—").length, "text-aurora-purple", Cloud],
  ] : [];

  return (
    <AppShell mode="admin">
      {/* Header */}
      <div className="mb-6">
        <div className="font-mono text-xs text-aurora-purple tracking-[0.3em] uppercase mb-2">// Auto-tuning</div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">Bibliothèque</h1>
        <p className="text-sm text-zinc-400 mt-1">Gestion centralisée des fichiers originaux (ORI) et de leurs modifications.</p>
      </div>

      {/* Compact KPI strip (always visible) */}
      {cov && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="kpi-strip">
          {covKpis.map(([label, val, color, Icon]) => (
            <div key={label} className="glass rounded-md p-4 flex items-center gap-3" data-testid="coverage-kpi">
              <div className={`w-9 h-9 grid place-items-center rounded-sm bg-white/5 ${color}`}><Icon className="w-4 h-4" /></div>
              <div>
                <div className={`font-display text-2xl font-bold leading-none ${color}`}>{val}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sub-navigation */}
      <div className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto" data-testid="library-tabs">
        {TABS.map((t) => {
          const active = tab === t.id;
          const badge = t.id === "analytics" && flagged.length > 0 ? flagged.length : null;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-display uppercase tracking-wide transition-colors relative whitespace-nowrap flex items-center gap-2 ${active ? "text-aurora-cyan" : "text-zinc-400 hover:text-white"}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
              {badge && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-full bg-signal-error/20 text-signal-error">{badge}</span>}
              {active && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-aurora-cyan shadow-glow" />}
            </button>
          );
        })}
      </div>

      {/* ============ LIBRARY TAB ============ */}
      {tab === "library" && (
        <div className="glass rounded-md overflow-hidden" data-testid="library-view">
          <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-white/10 flex-wrap">
            <div className="relative w-full max-w-sm">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="solution-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher par réf (SOL-000123), marque, ECU..."
                className="w-full bg-black/40 border border-white/15 rounded-sm pl-10 pr-3 py-2 text-sm focus:border-aurora-cyan outline-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{grouped.length} original{grouped.length > 1 ? "s" : ""} · {solutions.length} fichier{solutions.length > 1 ? "s" : ""}</span>
              <button data-testid="library-import-cta" onClick={() => setTab("import")} className="flex items-center gap-1.5 px-3 py-2 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-xs rounded-sm hover:bg-cyan-300 transition-colors shadow-glow">
                <Plus className="w-3.5 h-3.5" /> Importer
              </button>
            </div>
          </div>
          {loading ? (
            <div className="font-mono text-xs text-zinc-500 py-12 text-center">Chargement...</div>
          ) : grouped.length === 0 ? (
            <div className="text-center py-16">
              <Library className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm mb-4">{query ? "Aucun fichier ne correspond à votre recherche." : "Aucune solution. Importez une paire (original + modifié) pour démarrer l'auto-tuning."}</p>
              {!query && <button onClick={() => setTab("import")} className="inline-flex items-center gap-2 px-5 py-2.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cyan-300 transition-colors shadow-glow"><Plus className="w-4 h-4" /> Importer une solution</button>}
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.hash} data-testid={`ori-group-${(g.hash || "").slice(0, 10)}`} className="border-b border-white/10 last:border-0">
                <div className="flex items-center gap-3 px-6 py-3 bg-white/[0.03] flex-wrap">
                  <Fingerprint className="w-4 h-4 text-aurora-purple shrink-0" />
                  <span className="font-display uppercase tracking-tight text-white flex items-center gap-2"><Zap className="w-4 h-4 text-signal-success" /> {g.brand || "—"}{g.model ? <span className="text-aurora-cyan normal-case">{g.model}</span> : null}</span>
                  <span className="font-mono text-xs text-zinc-400">{g.ecu || "—"}</span>
                  <span className="font-mono text-[10px] text-zinc-500 truncate">{(g.hash || "").slice(0, 14)}… · {g.size}o</span>
                  {g.ori_filename && (
                    <span data-testid={`ori-filename-${(g.hash || "").slice(0, 10)}`} title={`Fichier original : ${g.ori_filename}`} className="font-mono text-[10px] text-aurora-purple bg-aurora-purple/10 border border-aurora-purple/25 px-2 py-0.5 rounded-sm truncate max-w-[240px]"><span className="text-aurora-purple/60">ORI·</span> {g.ori_filename}</span>
                  )}
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-aurora-cyan bg-aurora-cyan/10 px-2 py-0.5 rounded-sm shrink-0">{g.variants.length} variante{g.variants.length > 1 ? "s" : ""}</span>
                </div>
                <SolutionCombiner group={g} />
                {g.variants.map((s) => (
                  <div key={s.id} data-testid={`solution-row-${s.id}`} onClick={() => setDetailId(s.id)} className="flex items-center gap-4 px-6 py-3 pl-10 border-t border-white/5 hover:bg-white/[0.04] transition-colors cursor-pointer group">
                    <div className="flex items-center gap-1.5 w-28 shrink-0">
                      <Hash className="w-3.5 h-3.5 text-aurora-cyan shrink-0" />
                      <span className="font-mono text-xs font-bold text-aurora-cyan tracking-wide truncate group-hover:underline">{s.ref || "—"}</span>
                    </div>
                    <div className="flex flex-col gap-1 flex-1 min-w-0">
                      <div className="flex flex-wrap gap-1.5">
                        {s.services.map((sv) => <span key={sv} className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-aurora-cyan/30 text-aurora-cyan rounded-sm">{sv}</span>)}
                      </div>
                      {s.mod_filename && <span className="font-mono text-[10px] text-zinc-500 truncate"><span className="text-signal-success/70">MOD·</span> {s.mod_filename}</span>}
                    </div>
                    <div className="hidden sm:block shrink-0"><ReliabilityBadge s={s} /></div>
                    <div className="font-mono text-[10px] shrink-0 w-20 text-right">
                      {s.diff_mode === "diff" ? <span className="text-signal-success">{s.diff_count} o</span> : <span className="text-signal-processing">clone</span>}
                    </div>
                    <button data-testid={`inspect-solution-${s.id}`} onClick={(e) => { e.stopPropagation(); setDetailId(s.id); }} title="Inspecter / vérifier les fichiers" className="w-8 h-8 grid place-items-center border border-white/15 rounded-sm hover:border-aurora-cyan hover:text-aurora-cyan transition-colors shrink-0"><Eye className="w-4 h-4" /></button>
                    <button data-testid={`delete-solution-${s.id}`} onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="w-8 h-8 grid place-items-center border border-white/15 rounded-sm hover:border-signal-error hover:text-signal-error transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}

      {/* ============ IMPORT TAB ============ */}
      {tab === "import" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="import-view">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="import-launcher">
              {IMPORT_METHODS.map((m) => {
                const active = importMethod === m.id;
                return (
                  <button
                    key={m.id}
                    data-testid={`import-method-${m.id}`}
                    onClick={() => setImportMethod(active ? null : m.id)}
                    className={`glass p-5 rounded-md text-left border transition-all group flex items-start gap-4 ${active ? `border-${m.color} shadow-glow` : "border-white/10 hover:border-aurora-cyan/60"}`}
                  >
                    <div className={`w-11 h-11 grid place-items-center rounded-sm bg-white/5 text-${m.color} shrink-0`}><m.icon className="w-5 h-5" /></div>
                    <div>
                      <div className="font-display text-sm font-bold uppercase tracking-tight text-white">{m.title}</div>
                      <div className="font-mono text-[10px] text-zinc-500 mt-1">{m.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {importMethod === "ai" && <SmartImportWizard services={services} onImported={() => { load(); loadCov(); loadJobs(); }} />}
            {importMethod === "variantes" && <MultiVariantImport services={services} onImported={() => { load(); loadCov(); }} />}
            {importMethod === "mod" && <ModImport services={services} onImported={() => { load(); loadCov(); }} />}

            {importMethod === "paire" && (
              <div className="glass rounded-md p-6" data-testid="import-form">
                <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4">Importer une solution (paire)</h3>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Marque (info)</label>
                    <input data-testid="import-brand" className={inputCls} placeholder="Audi" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">ECU (info)</label>
                    <input data-testid="import-ecu" className={inputCls} placeholder="Bosch EDC17C64" value={form.ecu} onChange={(e) => setForm({ ...form, ecu: e.target.value })} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Logiciel utilisé <span className="text-zinc-600">(admin · optionnel)</span></label>
                    <input data-testid="import-tuned-with" className={inputCls} placeholder="Ex. WinOLS" value={form.tuned_with} onChange={(e) => setForm({ ...form, tuned_with: e.target.value })} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Puissance <span className="text-zinc-600">(ch · optionnel)</span></label>
                    <input data-testid="import-power-hp" type="number" min="0" className={inputCls} placeholder="Ex. 240" value={form.power_hp} onChange={(e) => setForm({ ...form, power_hp: e.target.value })} />
                  </div>
                </div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 block">Solutions (auto uniquement)</label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {services.map((s) => {
                    const on = form.services.includes(s.id);
                    return (
                      <button key={s.id} data-testid={`import-svc-${s.id}`} onClick={() => toggleSvc(s.id)} className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest rounded-sm border transition-colors ${on ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-5">
                  <div>
                    <input ref={origRef} type="file" className="hidden" data-testid="import-orig-input" onChange={(e) => setOrigFile(e.target.files[0])} />
                    <button onClick={() => origRef.current?.click()} className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-white/15 hover:border-aurora-cyan/50 transition-colors">
                      <FileUp className="w-5 h-5 text-zinc-400" />
                      <span className="font-mono text-xs text-zinc-300 truncate">{origFile ? origFile.name : "Fichier ORIGINAL (stock)"}</span>
                    </button>
                  </div>
                  <div>
                    <input ref={modRef} type="file" className="hidden" data-testid="import-mod-input" onChange={(e) => setModFile(e.target.files[0])} />
                    <button onClick={() => modRef.current?.click()} className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-signal-success/40 hover:border-signal-success transition-colors">
                      <FileUp className="w-5 h-5 text-signal-success" />
                      <span className="font-mono text-xs text-zinc-300 truncate">{modFile ? modFile.name : "Fichier MODIFIÉ (traité)"}</span>
                    </button>
                  </div>
                </div>
                <p className="font-mono text-[10px] text-zinc-500 mb-4">⚠️ Les deux fichiers doivent avoir exactement la même taille (le différentiel se calcule octet par octet).</p>
                <button data-testid="submit-import-btn" onClick={submitImport} disabled={importing} className="flex items-center gap-2 px-6 py-3 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-cyan-300 transition-colors shadow-glow disabled:opacity-60">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Importer la solution
                </button>
              </div>
            )}

            {importMethod === "masse" && (
              <div className="glass rounded-md p-6" data-testid="bulk-form">
                <div className="flex items-center gap-2 mb-1">
                  <Cloud className="w-5 h-5 text-aurora-purple" />
                  <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Import en masse · bibliothèque</h3>
                </div>
                <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-snug">
                  Envoyez une archive <b className="text-white">.zip / .rar / .7z</b> (ou un lien) contenant des dossiers avec paires <b>original + modifié</b> (mêmes tailles). Les solutions sont <b className="text-aurora-cyan">détectées automatiquement</b> via les mots-clés des noms de fichiers (ex. <span className="text-aurora-cyan">stage1</span>, <span className="text-aurora-cyan">dpf</span>, <span className="text-aurora-cyan">egr</span>). Fichiers indexés et stockés dans le <b className="text-aurora-purple">cloud</b>.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Marque (info)</label>
                    <input data-testid="bulk-brand" className={inputCls} placeholder="VW" value={bulk.brand} onChange={(e) => setBulk({ ...bulk, brand: e.target.value })} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">ECU (info)</label>
                    <input data-testid="bulk-ecu" className={inputCls} placeholder="EDC17" value={bulk.ecu} onChange={(e) => setBulk({ ...bulk, ecu: e.target.value })} />
                  </div>
                </div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Solutions de repli (optionnel)</label>
                <p className="font-mono text-[9px] text-zinc-600 mb-2 leading-snug">Utilisées uniquement pour les fichiers dont le nom ne contient aucun mot-clé reconnu. Sinon, la détection auto par mots-clés prime.</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {services.map((s) => {
                    const on = bulk.services.includes(s.id);
                    return (
                      <button key={s.id} data-testid={`bulk-svc-${s.id}`} onClick={() => toggleBulkSvc(s.id)} className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest rounded-sm border transition-colors ${on ? "border-aurora-purple text-aurora-purple bg-aurora-purple/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-5">
                  <div>
                    <input ref={bulkRef} type="file" accept=".zip,.rar,.7z" className="hidden" data-testid="bulk-file-input" onChange={(e) => setBulkFile(e.target.files[0])} />
                    <button onClick={() => bulkRef.current?.click()} className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-aurora-purple/40 hover:border-aurora-purple transition-colors">
                      <UploadCloud className="w-5 h-5 text-aurora-purple" />
                      <span className="font-mono text-xs text-zinc-300 truncate">{bulkFile ? bulkFile.name : "Archive .zip / .rar / .7z de paires"}</span>
                    </button>
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block flex items-center gap-1"><Link2 className="w-3 h-3" /> ou lien de téléchargement (gros packs)</label>
                    <input data-testid="bulk-url" className={inputCls} placeholder="https://.../pack.zip" value={bulk.url} onChange={(e) => setBulk({ ...bulk, url: e.target.value })} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button data-testid="submit-bulk-btn" onClick={submitBulk} disabled={bulkBusy} className="flex items-center gap-2 px-6 py-3 bg-aurora-purple text-white font-display font-bold uppercase tracking-wider rounded-sm hover:opacity-90 transition-opacity disabled:opacity-60">
                    {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Lancer l'import
                  </button>
                </div>

                {/* Folder import (subfolders, no zip needed) */}
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <FolderUp className="w-4 h-4 text-aurora-cyan" />
                    <h4 className="font-display text-sm font-semibold uppercase tracking-tight">Ou importez un dossier (avec sous-dossiers)</h4>
                  </div>
                  <p className="font-mono text-[10px] text-zinc-500 mb-3 leading-snug">Pas besoin de .zip : sélectionnez un <b className="text-aurora-cyan">dossier</b> (avec sous-dossiers de paires). <b className="text-white">Ajoutez-en plusieurs</b> l'un après l'autre — ils s'accumulent. Envoi par lots (aucune limite de taille). Marque + modèle + ECU détectés depuis les noms.</p>
                  <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden" data-testid="folder-input" onChange={(e) => {
                    const incoming = Array.from(e.target.files || []).filter((f) => REL_OK(f.name) && f.size > 0);
                    setFolderFiles((prev) => {
                      const seen = new Set((prev || []).map((f) => f.webkitRelativePath || f.name));
                      const merged = [...(prev || [])];
                      incoming.forEach((f) => { const k = f.webkitRelativePath || f.name; if (!seen.has(k)) { seen.add(k); merged.push(f); } });
                      return merged;
                    });
                    if (folderRef.current) folderRef.current.value = "";
                  }} />
                  {(() => {
                    const arr = Array.from(folderFiles || []);
                    const tops = new Set(arr.map((f) => (f.webkitRelativePath || f.name).split("/")[0]));
                    return (
                      <div className="flex flex-wrap items-center gap-3">
                        <button onClick={() => folderRef.current?.click()} data-testid="pick-folder-btn" className="flex items-center gap-3 p-3 rounded-sm border border-dashed border-aurora-cyan/40 hover:border-aurora-cyan transition-colors">
                          <FolderUp className="w-5 h-5 text-aurora-cyan" />
                          <span className="font-mono text-xs text-zinc-300 truncate">{arr.length ? `+ Ajouter un dossier` : "Choisir un dossier"}</span>
                        </button>
                        {arr.length > 0 && (
                          <>
                            <span data-testid="folder-selection-info" className="font-mono text-[11px] text-aurora-cyan">{tops.size} dossier(s) · {arr.length} fichiers</span>
                            <button data-testid="clear-folders-btn" onClick={() => { setFolderFiles([]); if (folderRef.current) folderRef.current.value = ""; }} className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-signal-error transition-colors">Réinitialiser</button>
                          </>
                        )}
                        <button data-testid="submit-folder-btn" onClick={() => uploadAndPreview(false)} disabled={bulkBusy || arr.length === 0}
                          className="flex items-center gap-2 px-6 py-3 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-cyan-300 transition-colors disabled:opacity-40">
                          {bulkBusy && folderProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderUp className="w-4 h-4" />}
                          {folderProgress ? `Envoi ${folderProgress.done}/${folderProgress.total}` : "Analyser les dossiers"}
                        </button>
                      </div>
                    );
                  })()}
                  {/* Mode professionnel : import direct sans revue */}
                  <div className="mt-4 p-3 rounded-sm border border-signal-success/30 bg-signal-success/5">
                    <div className="flex items-center gap-2 mb-1">
                      <ShieldCheck className="w-4 h-4 text-signal-success" />
                      <h5 className="font-display text-xs font-semibold uppercase tracking-tight text-signal-success">Mode professionnel — validation d'office</h5>
                    </div>
                    <p className="font-mono text-[10px] text-zinc-400 mb-2 leading-snug">Vous êtes certain que vos dossiers contiennent les bonnes paires <b className="text-white">ORI + MOD</b> ? Importez directement, sans revue manuelle : chaque paire détectée est validée et enregistrée automatiquement.</p>
                    <button data-testid="submit-folder-pro-btn" onClick={() => uploadAndPreview(true)} disabled={bulkBusy || !folderFiles || folderFiles.length === 0}
                      className="flex items-center gap-2 px-5 py-2.5 border border-signal-success text-signal-success font-display font-bold uppercase tracking-wider text-[11px] rounded-sm hover:bg-signal-success/10 transition-colors disabled:opacity-40">
                      {bulkBusy && folderProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                      {folderProgress ? `Envoi ${folderProgress.done}/${folderProgress.total}` : "Importer & valider d'office (Pro)"}
                    </button>
                  </div>
                  {proSummary && (
                    <div data-testid="pro-summary" className="mt-4 p-4 rounded-sm border border-signal-success/40 bg-black/40">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="w-4 h-4 text-signal-success" />
                          <h5 className="font-display text-sm font-semibold uppercase tracking-tight">Import Pro terminé</h5>
                        </div>
                        <button data-testid="close-pro-summary" onClick={() => setProSummary(null)} className="text-zinc-500 hover:text-white font-mono text-[10px] uppercase tracking-widest">Fermer</button>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2 font-mono text-[11px]">
                        <span className="px-2 py-1 rounded-sm bg-signal-success/15 text-signal-success">{proSummary.imported} importées</span>
                        {(proSummary.skipped.length + (proSummary.jobSkipped || 0)) > 0 && (
                          <span className="px-2 py-1 rounded-sm bg-signal-error/15 text-signal-error">{proSummary.skipped.length + (proSummary.jobSkipped || 0)} ignorées</span>
                        )}
                      </div>
                      {proSummary.skipped.length > 0 && (
                        <div className="mt-1">
                          <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Fichiers ignorés (à revoir manuellement)</p>
                          <ul className="max-h-40 overflow-auto space-y-0.5">
                            {proSummary.skipped.map((s, i) => (
                              <li key={i} data-testid={`skipped-${i}`} className="font-mono text-[10px] text-zinc-400 flex items-center gap-2">
                                <AlertTriangle className="w-3 h-3 text-signal-error shrink-0" /> {s.file} — <span className="text-zinc-500">{s.reason}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  {folderProgress && (
                    <div className="mt-3" data-testid="upload-progress">
                      <div className="flex items-center justify-between mb-1 font-mono text-[10px] text-zinc-400">
                        <span>Lot {folderProgress.batch || 0}/{folderProgress.batches || "?"} · {folderProgress.done}/{folderProgress.total} fichiers</span>
                        <span className="text-aurora-cyan">{((folderProgress.sentBytes || 0) / 1048576).toFixed(0)} / {((folderProgress.totalBytes || 0) / 1048576).toFixed(0)} Mo</span>
                      </div>
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-aurora-cyan transition-all" style={{ width: `${Math.round(((folderProgress.sentBytes || 0) / Math.max(1, folderProgress.totalBytes || 1)) * 100)}%` }} />
                      </div>
                      <div className="mt-1 font-mono text-[9px] text-zinc-500">
                        {folderProgress.done >= folderProgress.total ? "Analyse en cours…" : `Envoi en cours… ${Math.round(((folderProgress.sentBytes || 0) / Math.max(1, folderProgress.totalBytes || 1)) * 100)}%`}
                      </div>
                    </div>
                  )}

                  {preview && (
                    <div className="mt-5" data-testid="import-preview">
                      <datalist id="cat-brands-dl">{catBrands.map((b) => <option key={b} value={b} />)}</datalist>
                      <div className="glass rounded-sm p-3 mb-3 flex flex-wrap items-end gap-2" data-testid="bulk-apply-bar">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 w-full mb-0.5">Appliquer à toutes les lignes (laisser vide pour ignorer)</div>
                        <input value={bulkApply.brand} onChange={(e) => setBulkApply({ ...bulkApply, brand: e.target.value })} placeholder="Marque" list="cat-brands-dl" data-testid="bulk-brand" className="flex-1 min-w-[110px] bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                        <input value={bulkApply.model} onChange={(e) => setBulkApply({ ...bulkApply, model: e.target.value })} placeholder="Modèle" list="bulk-models-dl" data-testid="bulk-model" className="flex-1 min-w-[110px] bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                        <datalist id="bulk-models-dl">{(catModels[(bulkApply.brand || "").toLowerCase()] || []).map((m) => <option key={m} value={m} />)}</datalist>
                        <input value={bulkApply.ecu} onChange={(e) => setBulkApply({ ...bulkApply, ecu: e.target.value })} placeholder="ECU / calculateur" data-testid="bulk-ecu" className="flex-1 min-w-[110px] bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                        <input value={bulkApply.power_hp} onChange={(e) => setBulkApply({ ...bulkApply, power_hp: e.target.value })} type="number" min="0" placeholder="Puissance (ch)" data-testid="bulk-power" className="w-[130px] bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                        <input value={bulkApply.torque_nm} onChange={(e) => setBulkApply({ ...bulkApply, torque_nm: e.target.value })} type="number" min="0" placeholder="Couple (Nm)" data-testid="bulk-torque" className="w-[130px] bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                        <input value={bulkApply.stage} onChange={(e) => setBulkApply({ ...bulkApply, stage: e.target.value })} placeholder="Stage" data-testid="bulk-stage" className="w-[110px] bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                        <button onClick={applyToAll} data-testid="apply-all-btn" className="flex items-center gap-1.5 px-3 py-1.5 border border-aurora-cyan/50 text-aurora-cyan font-mono text-[10px] uppercase tracking-widest rounded-sm hover:bg-aurora-cyan/10 transition-colors">
                          <RefreshCw className="w-3.5 h-3.5" /> Appliquer à toutes
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <BookOpen className="w-4 h-4 text-aurora-cyan" />
                        <h4 className="font-display text-sm font-semibold uppercase tracking-tight">Aperçu avant validation — {preview.pairs.filter((p) => p._keep).length}/{preview.pairs.length} paire(s)</h4>
                        {(() => {
                          const kept = preview.pairs.filter((p) => p._keep);
                          const complete = kept.filter(isComplete).length;
                          const todo = kept.length - complete;
                          return <span data-testid="preview-counter" className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-white/10"><span className="text-signal-success">{complete} complètes</span> · <span className={todo > 0 ? "text-signal-processing" : "text-zinc-500"}>{todo} à compléter</span></span>;
                        })()}
                        {preview.skipped.length > 0 && <span className="font-mono text-[10px] text-signal-processing">{preview.skipped.length} fichier(s) ignoré(s)</span>}
                        <div className="ml-auto flex gap-2">
                          <button data-testid="cancel-preview-btn" onClick={() => setPreview(null)} className="px-3 py-1.5 border border-white/15 text-zinc-400 font-mono text-[10px] uppercase tracking-widest rounded-sm hover:text-white transition-colors">Annuler</button>
                          {preview.pairs.some((p) => p._keep && !isComplete(p)) && (
                            <button data-testid="confirm-complete-btn" onClick={confirmComplete} disabled={committing}
                              title="Importe les lignes avec marque + modèle + solution ; garde les autres à compléter"
                              className="flex items-center gap-1.5 px-3 py-1.5 border border-aurora-cyan/60 text-aurora-cyan font-display font-bold uppercase tracking-wider text-[11px] rounded-sm hover:bg-aurora-cyan/10 transition-colors disabled:opacity-60">
                              {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Valider les lignes complètes ({preview.pairs.filter((p) => p._keep && isComplete(p)).length})
                            </button>
                          )}
                          <button data-testid="confirm-import-btn" onClick={confirmImport} disabled={committing}
                            className="flex items-center gap-1.5 px-4 py-1.5 bg-signal-success text-black font-display font-bold uppercase tracking-wider text-[11px] rounded-sm hover:opacity-90 transition-opacity disabled:opacity-60">
                            {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Confirmer l'import
                          </button>
                        </div>
                      </div>
                      {(() => {
                        const suspects = preview.pairs.filter((p) => p.changed_bytes === 0);
                        if (suspects.length === 0) return null;
                        const anyKept = suspects.some((p) => p._keep);
                        return (
                          <div data-testid="suspect-report" className="mb-3 rounded-sm border border-signal-error/40 bg-signal-error/10 px-3 py-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-signal-error shrink-0" />
                              <span className="font-mono text-[10px] uppercase tracking-widest text-signal-error">
                                {suspects.length} paire(s) suspecte(s) — ORI identique au MOD (0 octet modifié)
                              </span>
                              {anyKept && (
                                <button data-testid="deselect-suspect-btn"
                                  onClick={() => setPreview((pv) => ({ ...pv, pairs: pv.pairs.map((p) => p.changed_bytes === 0 ? { ...p, _keep: false } : p) }))}
                                  className="ml-auto px-2.5 py-1 border border-signal-error/50 text-signal-error font-mono text-[10px] uppercase tracking-widest rounded-sm hover:bg-signal-error/15 transition-colors">
                                  Décocher les paires identiques
                                </button>
                              )}
                            </div>
                            <ul className="mt-1.5 pl-6 space-y-0.5 max-h-[120px] overflow-y-auto">
                              {suspects.map((p, si) => (
                                <li key={si} data-testid={`suspect-${si}`} className="font-mono text-[10px] text-zinc-400 truncate">· {p.mod_name} <span className="text-zinc-600">↔ {p.ori_name}</span></li>
                              ))}
                            </ul>
                          </div>
                        );
                      })()}
                      {(() => {
                        // Collisions: kept pairs sharing the SAME original + SAME services will collapse
                        // onto a single solution (one silently overwrites the others). Warn + resolve.
                        const clashes = getClashes(preview.pairs);
                        if (clashes.length === 0) return null;
                        return (
                          <div data-testid="collision-report" className="mb-3 rounded-sm border border-signal-processing/50 bg-signal-processing/10 px-3 py-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-signal-processing shrink-0" />
                              <span className="font-mono text-[10px] uppercase tracking-widest text-signal-processing">
                                {clashes.length} collision(s) — même original + mêmes services → une seule variante sera conservée
                              </span>
                              <button data-testid="auto-differentiate-btn" onClick={autoDifferentiate}
                                title="Détecter une option distinctive (Pop&Bang, EGR, FAP, Stage…) dans les noms et l'appliquer"
                                className="ml-auto flex items-center gap-1 px-2.5 py-1 border border-aurora-cyan/50 text-aurora-cyan font-mono text-[10px] uppercase tracking-widest rounded-sm hover:bg-aurora-cyan/10 transition-colors">
                                <Wand2 className="w-3 h-3" /> Différencier auto
                              </button>
                            </div>
                            <p className="mt-1 pl-6 font-mono text-[9px] text-zinc-500 leading-snug">
                              « Différencier auto » tente d'ajouter une option distinctive depuis le nom. Sinon, choisissez la variante à <b className="text-zinc-300">garder</b> (les autres seront ignorées).
                            </p>
                            <ul className="mt-1.5 pl-6 space-y-1.5 max-h-[180px] overflow-y-auto">
                              {clashes.map(([k, arr], gi) => (
                                <li key={gi} data-testid={`collision-group-${gi}`} className="font-mono text-[10px] text-zinc-400">
                                  <span className="text-signal-processing">{(arr[0].services || []).join("+") || "—"}</span> · original <span className="text-zinc-500">{arr[0].ori_name}</span>
                                  <div className="pl-3 mt-0.5 space-y-1">
                                    {arr.map((p, pi) => (
                                      <div key={pi} className="flex items-center gap-2">
                                        <span className="text-zinc-500 truncate flex-1">· {p.mod_name}</span>
                                        <button data-testid={`keep-pair-${gi}-${pi}`} onClick={() => keepOnlyInGroup(p.mod_rel || p.mod_name)}
                                          className="shrink-0 px-2 py-0.5 border border-signal-success/50 text-signal-success font-mono text-[9px] uppercase tracking-widest rounded-sm hover:bg-signal-success/15 transition-colors">
                                          Garder celle-ci
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })()}
                      {preview.skipped.length > 0 && (
                        <details data-testid="skipped-report" className="mb-3 glass rounded-sm border border-signal-processing/25">
                          <summary className="cursor-pointer select-none px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-signal-processing flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            {preview.pairs.length} paire(s) détectée(s) · {preview.skipped.length} fichier(s) ignoré(s) — voir le détail
                          </summary>
                          <ul className="px-3 pb-3 pt-1 space-y-1 max-h-[200px] overflow-y-auto">
                            {Object.entries(preview.skipped.reduce((acc, s) => { (acc[s.reason] = acc[s.reason] || []).push(s.file); return acc; }, {})).map(([reason, filesArr], gi) => (
                              <li key={gi} data-testid={`skipped-group-${gi}`} className="font-mono text-[10px] text-zinc-400">
                                <span className="text-signal-processing">{reason}</span> <span className="text-zinc-600">({filesArr.length})</span>
                                <div className="pl-3 mt-0.5 space-y-0.5">
                                  {filesArr.map((f, fi) => (
                                    <div key={fi} className="text-zinc-500 truncate">· {f}</div>
                                  ))}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                      <div className="border border-white/10 rounded-sm overflow-hidden max-h-[460px] overflow-y-auto">
                        {preview.pairs.map((p, i) => (
                          <div key={i} data-testid={`preview-pair-${i}`} className={`p-3 border-b border-white/5 ${p._keep ? "" : "opacity-40"}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <button onClick={() => updatePair(i, { _keep: !p._keep })} title={p._keep ? "Exclure" : "Inclure"}
                                className={`w-6 h-6 grid place-items-center rounded-sm border shrink-0 ${p._keep ? "border-signal-success/50 text-signal-success bg-signal-success/10" : "border-white/15 text-zinc-500"}`}>
                                {p._keep ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                              </button>
                              <div className="min-w-0 flex-1">
                                <div className="font-mono text-[10px] text-zinc-300 truncate"><span className="text-aurora-purple/70">ORI</span> {p.ori_name}</div>
                                <div className="font-mono text-[10px] text-zinc-500 truncate"><span className="text-signal-success/70">MOD</span> {p.mod_name} · {p.size}o{p.changed_bytes != null && (p.changed_bytes > 0 ? <span data-testid={`preview-diff-${i}`} className="text-signal-success"> · {p.changed_bytes.toLocaleString()} octets modifiés</span> : <span data-testid={`preview-diff-${i}`} className="text-signal-error"> · ⚠ identique (0 octet modifié)</span>)}</div>
                                {p.sources && (() => {
                                  const labels = { brand: "marque", model: "modèle", ecu: "ECU", power_hp: "puissance", torque_nm: "couple", stage: "stage", services: "service" };
                                  const fromFolder = Object.keys(labels).filter((k) => p.sources[k] === "folder");
                                  return fromFolder.length > 0 ? (
                                    <div data-testid={`preview-source-${i}`} className="flex items-center gap-1 mt-0.5 font-mono text-[9px] text-aurora-cyan/80"><FolderUp className="w-3 h-3" /> Déduit du dossier : {fromFolder.map((k) => labels[k]).join(", ")}</div>
                                  ) : null;
                                })()}
                              </div>
                            </div>
                            <div className="grid sm:grid-cols-4 gap-2 mb-2">
                              <input value={p.brand || ""} onChange={(e) => updatePair(i, { brand: e.target.value })} placeholder="Marque" list="cat-brands-dl" data-testid={`preview-brand-${i}`} className="bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                              <input value={p.model || ""} onChange={(e) => updatePair(i, { model: e.target.value })} placeholder="Modèle" list={`cat-models-${i}`} data-testid={`preview-model-${i}`} className="bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                              <input value={p.ecu || ""} onChange={(e) => updatePair(i, { ecu: e.target.value })} placeholder="ECU / calculateur" data-testid={`preview-ecu-${i}`} className="bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                              <input value={p.power_hp || ""} onChange={(e) => updatePair(i, { power_hp: e.target.value })} type="number" min="0" placeholder="Puissance (ch)" data-testid={`preview-power-${i}`} className="bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                              <datalist id={`cat-models-${i}`}>
                                {(catModels[(p.brand || "").toLowerCase()] || []).map((m) => <option key={m} value={m} />)}
                              </datalist>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-2 mb-2">
                              <input value={p.torque_nm || ""} onChange={(e) => updatePair(i, { torque_nm: e.target.value })} type="number" min="0" placeholder="Couple (Nm)" data-testid={`preview-torque-${i}`} className="bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                              <input value={p.stage || ""} onChange={(e) => updatePair(i, { stage: e.target.value })} placeholder="Stage" data-testid={`preview-stage-${i}`} className="bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-2 mb-2">
                              <input value={p.base_power_hp || ""} onChange={(e) => updatePair(i, { base_power_hp: e.target.value })} type="number" min="0" placeholder="Puissance origine (ch)" data-testid={`preview-base-power-${i}`} className="bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                              <input value={p.base_torque_nm || ""} onChange={(e) => updatePair(i, { base_torque_nm: e.target.value })} type="number" min="0" placeholder="Couple origine (Nm)" data-testid={`preview-base-torque-${i}`} className="bg-black/40 border border-white/10 focus:border-aurora-cyan/60 rounded-sm px-2 py-1 font-mono text-[11px] text-white outline-none" />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {services.map((s) => {
                                const on = (p.services || []).includes(s.id);
                                return <button key={s.id} onClick={() => togglePairSvc(i, s.id)} className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest rounded-sm border transition-colors ${on ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-500 hover:border-white/30"}`}>{s.name}</button>;
                              })}
                              {(p.services || []).length === 0 && <span className="font-mono text-[9px] text-signal-error uppercase tracking-widest self-center">⚠ aucune solution — sera ignorée</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!importMethod && (
              <div className="glass rounded-md p-6 text-center" data-testid="import-hint">
                <UploadCloud className="w-9 h-9 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">Choisissez une méthode d'import ci-dessus pour commencer.</p>
              </div>
            )}
          </div>

          {/* Recent imports sidebar */}
          <div className="space-y-4" data-testid="import-jobs">
            <div className="flex items-center justify-between">
              <h3 className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Imports récents</h3>
              <button onClick={loadJobs} className="text-zinc-500 hover:text-white"><RefreshCw className="w-3.5 h-3.5" /></button>
            </div>
            {jobs.length === 0 ? (
              <div className="glass rounded-md p-4 font-mono text-[10px] text-zinc-600">Aucun import récent.</div>
            ) : (
              jobs.slice(0, 8).map((j) => (
                <div key={j.id} data-testid={`job-${j.id}`} className="glass rounded-md p-3 text-xs font-mono">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-zinc-300 truncate">{j.brand || "—"} · {j.source}</span>
                    {j.status === "running" ? <span className="flex items-center gap-1 text-aurora-cyan shrink-0"><Loader2 className="w-3 h-3 animate-spin" /> en cours</span>
                      : j.status === "failed" ? <span className="text-signal-error shrink-0">échec</span>
                      : <span className="text-zinc-500 shrink-0">terminé</span>}
                  </div>
                  <div className="text-zinc-500 truncate">{(j.services || []).join(", ")}</div>
                  <div className="flex gap-3 mt-1">
                    <span className="text-signal-success">{j.imported} ok</span>
                    {j.skipped > 0 && <span className="text-signal-processing">{j.skipped} ignorés</span>}
                  </div>
                  {j.fail_summary && (
                    <div data-testid={`job-fail-${j.id}`} className="mt-1.5 flex items-start gap-1.5 text-[10px] leading-snug text-signal-error border-t border-signal-error/20 pt-1.5">
                      <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                      <span>{j.fail_summary}</span>
                    </div>
                  )}
                  {j.status === "failed" && j.error && !j.fail_summary && (
                    <div className="mt-1.5 text-[10px] text-signal-error truncate">{j.error}</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ============ ANALYTICS TAB ============ */}
      {tab === "analytics" && (
        <div className="space-y-6" data-testid="analytics-view">
          <div className="glass rounded-md p-4 border-l-2 border-signal-success flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-signal-success shrink-0 mt-0.5" />
            <p className="text-xs text-zinc-300 leading-relaxed">
              <b className="text-white">Sécurité :</b> une solution n'est livrée <b>automatiquement</b> que si le fichier original entrant est <b className="text-signal-success">identique au bit près</b> (empreinte SHA-256) à une référence. Sinon : référence pour le technicien, jamais de livraison aveugle.
            </p>
          </div>

          {flagged.length > 0 && (
            <div className="glass rounded-md p-5 border-l-2 border-signal-error" data-testid="defect-alert">
              <div className="flex items-center gap-2 mb-1">
                <ShieldAlert className="w-5 h-5 text-signal-error" />
                <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Fichiers à surveiller · taux de défauts &gt; 10%</h3>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-signal-error bg-signal-error/10 px-2 py-0.5 rounded-sm">{flagged.length}</span>
              </div>
              <div className="space-y-2 mt-3">
                {flagged.map((s) => (
                  <div key={s.id} data-testid={`flagged-${s.id}`} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 last:border-0">
                    <span className="font-mono text-xs text-zinc-300 truncate">
                      <span className="text-aurora-cyan font-bold">{s.ref}</span> · {s.brand || "—"}{s.ecu ? ` · ${s.ecu}` : ""}
                    </span>
                    <span className="flex items-center gap-3 shrink-0">
                      <span className="font-mono text-[10px] text-signal-error">{s.reliability}% · {s.defects}/{s.deliveries} défauts</span>
                      <button onClick={() => setDetailId(s.id)}
                        className="font-mono text-[10px] uppercase tracking-widest text-aurora-purple border border-aurora-purple/40 hover:bg-aurora-purple/10 px-2 py-0.5 rounded-sm transition-colors">
                        Inspecter / restaurer
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {cov && cov.missing_requests && cov.missing_requests.length > 0 && (
            <div className="glass rounded-md p-5 border-l-2 border-signal-processing" data-testid="missing-requests">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-5 h-5 text-signal-processing" />
                <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Demandes sans solution en base</h3>
              </div>
              <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-snug">
                Véhicules/ECU demandés par des clients mais <b className="text-white">non livrés automatiquement</b>. Priorise l'import de ces données pour automatiser plus de revenus.
              </p>
              <div className="space-y-2">
                {cov.missing_requests.map((m, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 last:border-0" data-testid="missing-row">
                    <span className="font-mono text-xs text-zinc-300 truncate">
                      <span className="text-white font-bold">{m.brand}</span>{m.model ? ` ${m.model}` : ""}{m.ecu ? ` · ${m.ecu}` : ""}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-signal-processing bg-signal-processing/10 px-2 py-0.5 rounded-sm">
                        {m.count} demande{m.count > 1 ? "s" : ""}
                      </span>
                      <button data-testid="prefill-import-btn" onClick={() => prefillImport(m)}
                        className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-aurora-purple border border-aurora-purple/40 hover:bg-aurora-purple/10 px-2 py-0.5 rounded-sm transition-colors">
                        <UploadCloud className="w-3 h-3" /> Importer
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {cov ? (
            <div className="glass rounded-md p-5" data-testid="coverage-panel">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-aurora-cyan" />
                <h3 className="font-mono text-[11px] uppercase tracking-widest text-zinc-300">Couverture par solution</h3>
              </div>
              <div className="space-y-2.5 mb-5">
                {cov.by_service.map((s) => {
                  const max = Math.max(1, ...cov.by_service.map((x) => x.count));
                  return (
                    <div key={s.id} className="flex items-center gap-3" data-testid={`coverage-svc-${s.id}`}>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 w-32 shrink-0 truncate">{s.name}</span>
                      <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-aurora-cyan rounded-full transition-all" style={{ width: `${(s.count / max) * 100}%` }} />
                      </div>
                      <span className="font-mono text-xs text-zinc-300 w-8 text-right">{s.count}</span>
                    </div>
                  );
                })}
              </div>
              <div className="grid sm:grid-cols-2 gap-5 pt-4 border-t border-white/5">
                {[["Top ECU", cov.by_ecu], ["Top marques", cov.by_brand]].map(([title, rows]) => (
                  <div key={title}>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{title}</div>
                    {rows.filter((r) => r.name !== "—").slice(0, 6).map((r) => (
                      <div key={r.name} className="flex justify-between font-mono text-xs py-1 border-b border-white/5 last:border-0">
                        <span className="text-zinc-300 truncate">{r.name}</span>
                        <span className="text-aurora-cyan">{r.count}</span>
                      </div>
                    ))}
                    {rows.filter((r) => r.name !== "—").length === 0 && <div className="font-mono text-[10px] text-zinc-600">Aucune donnée</div>}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="glass rounded-md p-8 text-center font-mono text-xs text-zinc-500">Chargement des statistiques...</div>
          )}
        </div>
      )}

      {/* ============ TOOLS TAB ============ */}
      {tab === "tools" && (
        <div className="space-y-6" data-testid="tools-view">
          <TuningStateChecker />
          <VehicleCatalog />
          <EcuCatalog />
          <ChecksumLearning />
          <KeywordDictionary services={services} />
        </div>
      )}
      {detailId && <SolutionDetail solutionId={detailId} services={services} onClose={() => setDetailId(null)} onSaved={() => { load(); loadCov(); }} />}
    </AppShell>
  );
};
